/**
 * Why a province is paying less than its card says, in words.
 *
 * This file used to carry a hand copy of `doIncome`'s scale, because the world
 * map wanted to show what a province pays and the simulation had no reader to
 * ask. It was the fourth such copy — `doIncome`, `projectedIncome` and
 * `provinceTradeValue` each had their own — and the trade split would have made
 * every one of them wrong in a different direction.
 *
 * `src/sim/campaign/holdings.ts` now holds the one expression and every caller
 * reads it, so the arithmetic that lived here is **deleted** rather than
 * corrected. `holdingIncome(c, p)` is the answer to "what does this province
 * pay": it owns the split and hands back the tax half and the market half as
 * two fields, already scaled by the holdings tier ladder.
 *
 * What is left here is the part that was never arithmetic: turning a scale below
 * one into a sentence a player can act on. That is presentation, it belongs to
 * the UI, and it reads the same province state the simulation does rather than
 * re-deriving anything from it.
 */

import { campYieldScale } from '../sim/campaign/bandits.ts';
import { holdingIncome } from '../sim/campaign/holdings.ts';
import type { ResourceKey } from '../sim/types.ts';
import type { CampaignState } from '../sim/campaign/types.ts';

/** The two rows of the province panel's income block, and what backs them. */
export interface HoldingSplit {
  /** "Pays you" — gold into the treasury at the next council. */
  tax: number;
  /** "To market" — gold onto the road, returned only through node control. */
  market: number;
  /** The two above, together. What the holding is worth in gold, full stop. */
  gross: number;
  levy: number;
  goods: Array<{ key: ResourceKey; amount: number }>;
}

/**
 * What the province panel puts on screen, in one place the tests can also call.
 *
 * This function exists because of how the panel was wrong the *second* time. It
 * was reading a trade-side reader that stopped being the one `doIncome` pays
 * out of when the holdings ladder landed — and the test file did not catch it,
 * because the test computed the expected figure the same way the panel did and
 * the two agreed with each other all the way down.
 *
 * So there is exactly one expression of "what the panel shows", the panel calls
 * it, and `tests/ui-income.test.ts` calls the same one and checks it against
 * `doIncome`'s own arithmetic. A test that re-derives the answer is a test that
 * agrees with the bug.
 *
 * **The split is not ours to make.** `TRADE_SHARE` is not referenced here and
 * neither half is derived from the other: `holdingIncome` returns both, and this
 * passes them through. That survives the share being retuned or made to vary by
 * node without anything in the UI noticing.
 */
export function holdingSplit(c: CampaignState, p: number): HoldingSplit {
  if ((c.provinces[p]?.owner ?? -1) < 0) {
    return { tax: 0, market: 0, gross: 0, levy: 0, goods: [] };
  }
  // `gold` is already the tax half, net of the share; `trade` is the rest.
  // Neither is derived here — this function's whole job is to be the one place
  // the panel and its test agree on what the two rows mean.
  const inc = holdingIncome(c, p);
  return {
    tax: inc.gold,
    market: inc.trade,
    gross: inc.gold + inc.trade,
    levy: inc.levy,
    goods: inc.goods,
  };
}

/**
 * Every reason the takings are short, largest first.
 *
 * Ordered by size because "unrest 42% · brigands take 10%" is the sentence that
 * tells a player which problem to go and solve. A province paying its full card
 * price returns the empty string — the absence of the line is the good news.
 *
 * Deliberately *not* a list of everything that reduces the number on screen:
 * the trade share is not in here. A third of a province's wealth going to its
 * market is not damage and must not be listed beside unrest and brigands as
 * though it were something to fix. It is reported in its own row, as a
 * destination rather than a loss.
 */
export function yieldCuts(c: CampaignState, p: number): string {
  const s = c.provinces[p];
  const cuts: Array<[number, string]> = [];
  if (s.development < 0.995) {
    cuts.push([1 - s.development, `undeveloped ${Math.round((1 - s.development) * 100)}%`]);
  }
  if (s.unrest > 0.005) cuts.push([s.unrest, `unrest ${Math.round(s.unrest * 100)}%`]);
  if (s.siegeBy >= 0) cuts.push([0.7, 'under siege, and paying almost nothing']);
  const camp = 1 - campYieldScale(c, p);
  if (camp > 0.005) cuts.push([camp, `brigands take ${Math.round(camp * 100)}%`]);
  if (cuts.length === 0) return '';
  cuts.sort((a, b) => b[0] - a[0]);
  return cuts.map((x) => x[1]).join(' · ');
}
